import { PHASE_NAME } from '@concord/domain';
import { Drawer, Icon, PhaseTag } from '@concord/ui';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useMe } from '../../lib/session';
import { AskAi } from './AskAi';
import { HELP, helpIdFor } from './content';
import s from './help.module.css';

interface HelpState {
  open: boolean;
  setOpen: (v: boolean) => void;
}

const HelpContext = createContext<HelpState>({ open: false, setOpen: () => {} });

/** One help drawer for the app; the topic follows the current screen. */
export function HelpProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  // A new screen closes help rather than showing the previous screen's topic.
  // biome-ignore lint/correctness/useExhaustiveDependencies: close on navigation only
  useEffect(() => setOpen(false), [pathname]);
  return (
    <HelpContext.Provider value={{ open, setOpen }}>
      {children}
      <HelpDrawer open={open} onOpenChange={setOpen} pathname={pathname} />
    </HelpContext.Provider>
  );
}

/** The Help button every header shows. */
export function HelpButton() {
  const { setOpen } = useContext(HelpContext);
  return (
    <button
      type="button"
      className={s.button}
      onClick={() => setOpen(true)}
      aria-haspopup="dialog"
      aria-label="Help"
    >
      <Icon.Help />
      <span className={s.text}>Help</span>
    </button>
  );
}

function HelpDrawer({
  open,
  onOpenChange,
  pathname,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  pathname: string;
}) {
  const id = helpIdFor(pathname);
  const topic = HELP[id];
  const { data: me } = useMe();
  // Ask AI is for navigators and admins: signed in to the console.
  const canAsk = me?.realm === 'staff' && pathname.startsWith('/console');
  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={topic.title}
      description={topic.summary}
      footer="Something unclear or not working? Tell us with Feedback at the bottom right."
    >
      {canAsk && <AskAi key={id} screen={id} />}
      {topic.sections.map((sec) => (
        <section key={sec.heading} className={s.section}>
          <h3>{sec.heading}</h3>
          <ul>
            {sec.points.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </section>
      ))}
      {topic.figures?.map((f) => (
        <figure key={f.src} className={s.figure}>
          <a href={f.src} target="_blank" rel="noopener" title="Open full size">
            <img src={f.src} alt={f.alt} loading="lazy" />
          </a>
          <figcaption>{f.caption}</figcaption>
        </figure>
      ))}
      {topic.later && topic.later.length > 0 && (
        <section className={s.section} aria-label="Coming in later phases">
          <h3>Coming later</h3>
          <ul className={s.later}>
            {topic.later.map((l) => (
              <li key={l.text}>
                <PhaseTag phase={l.phase} />
                <span>
                  {l.text}{' '}
                  <span className="sub2" aria-hidden="true">
                    ({PHASE_NAME[l.phase]})
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Drawer>
  );
}
